import { MARKER_Z_INDEX_BASE } from '~/constants/map'

type GoogleMapsServices = {
  autocomplete: typeof google.maps.places.AutocompleteSuggestion
  map: typeof google.maps.Map
  marker: typeof google.maps.marker.AdvancedMarkerElement
}

let googlemaps: GoogleMapsServices | undefined
let loadFailed = false
let loadPromise: Promise<boolean> | undefined

async function ensureGoogleMapsLoaded(apiKey: string): Promise<boolean> {
  if (googlemaps) {
    return true
  }

  if (loadFailed) {
    return false
  }

  loadPromise ??= (async () => {
    try {
      const { importLibrary, setOptions } = await import('@googlemaps/js-api-loader')

      setOptions({
        key: apiKey,
        libraries: ['places', 'geocoding'],
      })

      const places = importLibrary('places')
      const maps = importLibrary('maps')
      const marker = importLibrary('marker')
      const geometry = importLibrary('geometry')

      const { AutocompleteSuggestion } = await places
      const { Map } = await maps
      const { AdvancedMarkerElement } = await marker
      await geometry

      googlemaps = {
        autocomplete: AutocompleteSuggestion,
        map: Map,
        marker: AdvancedMarkerElement,
      }

      return true
    }
    catch {
      loadFailed = true

      return false
    }
  })()
  return loadPromise
}

export default async function useGoogleMaps(ignoreRouteCheck = false) {
  if (isMetaServer) {
    return
  }

  const apiKey = useRuntimeConfig().public.google.mapsApiKey
  const getRouteBaseName = useRouteBaseName()
  const route = useRoute()

  if (!ignoreRouteCheck && 'callback' === getRouteBaseName(route)) {
    return
  }

  const loaded = await ensureGoogleMapsLoaded(apiKey)
  if (!loaded || !googlemaps) {
    return
  }

  const services = googlemaps

  const placePredictions = async (
    input: string,
    countries: string | string[] | undefined,
    types?: string[],
    excludeTypes: string[] = [],
    language?: SupportedLocales,
  ): Promise<google.maps.places.PlacePrediction[]> => {
    const countryArr = typeof countries === 'string' ? [countries] : countries

    try {
      const { suggestions } = await services.autocomplete.fetchAutocompleteSuggestions(
        {
          input,
          includedRegionCodes: countryArr,
          includedPrimaryTypes: types,
          language,
        })

      if (!suggestions.length) {
        return []
      }

      // maps is necessary to force the render of the placePrediction
      // if return the suggestions directly
      // it will break vue component rendering even with null checks
      // that's also why the filter is applied later
      return suggestions
        .map(({ placePrediction }) =>
          placePrediction
            ? {
                distanceMeters: placePrediction.distanceMeters,
                placeId: placePrediction.placeId,
                text: placePrediction.text,
                mainText: placePrediction.mainText,
                types: placePrediction.types,
                secondaryText: placePrediction.secondaryText,
              }
            : undefined,
        )
        .filter((place): place is google.maps.places.PlacePrediction => {
          if (place === undefined) {
            return false
          }

          if (excludeTypes?.length) {
            // Exclude irrelevant types
            return !place?.types?.some((t) => excludeTypes.includes(t))
          }

          return true
        })
    }
    catch {
      return []
    }
  }

  const map = (element: HTMLElement, mapOptions: google.maps.MapOptions = {}) => {
    return new services.map(element, mapOptions)
  }

  const advancedMarker = ({
    map,
    position,
    content,
    zIndex = MARKER_Z_INDEX_BASE,
  }: {
    map?: google.maps.Map
    position: { lat: number, lng: number }
    content: HTMLElement
    zIndex?: number
  }) => {
    return new services.marker({
      map,
      position,
      content,
      zIndex,
    })
  }

  return {
    placePredictions,
    map,
    advancedMarker,
  }
}
